import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { RefreshCw, Search, GitBranch, Users, RefreshCcw, X } from 'lucide-react';
import ERPAdvancedGrid from '../components/erp/ERPAdvancedGrid';
import { erpToast } from '../components/erp/ERPToast';
import '../styles/erp-theme.css';
import { API, apiFetch } from '../lib/api';



const USER_TYPES = ['All', 'LICENSEE', 'ADMINSUPPORT', 'LICENSOR', 'MANAGER'];

const COLUMNS = [
  { field: 'ClientId',         header: 'Client ID',    visible: true, frozen: true },
  { field: 'UserType',         header: 'User Type',    visible: true },
  { field: 'ClientName',       header: 'Login Name',   visible: true },
  { field: 'EmailId',          header: 'Email',        visible: true },
  { field: 'MClientId',        header: 'ERP User ID',  visible: true },
  { field: 'Status',           header: 'Status',       visible: true },
  { field: 'IpAddress',        header: 'IP',           visible: true },
  { field: 'GlobalAddress',    header: 'Global IP',    visible: false },
  { field: 'ParentClientName', header: 'Company',      visible: true },
  { field: 'ParentEmail',      header: 'Parent Email', visible: false },
  { field: 'CreatedDate',      header: 'Created',      visible: true },
];

export default function SubUsersPage() {
  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  // Global search
  const [globalSearch, setGlobalSearch] = useState('');
  // Type filter
  const [typeFilter, setTypeFilter] = useState('All');
  // Grid state
  const [page, setPage]         = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortField, setSortField] = useState('CreatedDate');
  const [sortDir, setSortDir]   = useState('desc');
  const [colFilters, setColFilters] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiFetch(`${API}/licensor/child-users`);
      const d = await r.json();
      setAllRows(Array.isArray(d) ? d : []);
    } catch (e) {
      erpToast.error('Failed to load users: ' + e.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const syncNow = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const r = await apiFetch(`${API}/licensor/sync-tenant-users`, { method: 'POST' });
      const d = await r.json();
      if (d.error) {
        erpToast.error('Sync failed: ' + d.error);
      } else {
        setSyncResult(d);
        erpToast.success(`Sync complete — ${d.synced} user(s) synced across ${d.clients} client(s)`);
        load();
      }
    } catch (e) {
      erpToast.error('Sync failed: ' + e.message);
    }
    setSyncing(false);
  };

  const onSort = (f) => {
    if (sortField === f) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(f); setSortDir('asc'); }
  };

  // Apply global search + type filter + column filters + sort
  const filtered = useMemo(() => {
    let rows = allRows;

    // Type filter
    if (typeFilter !== 'All') {
      rows = rows.filter(r => r.UserType === typeFilter);
    }

    // Global search (any field)
    if (globalSearch.trim()) {
      const q = globalSearch.toLowerCase();
      rows = rows.filter(r =>
        Object.values(r).some(v => v != null && String(v).toLowerCase().includes(q))
      );
    }

    // Column-level filters
    const af = Object.entries(colFilters).filter(([, v]) => v != null && String(v).trim() !== '');
    if (af.length) {
      rows = rows.filter(r => af.every(([k, v]) => String(r[k] ?? '').toLowerCase().includes(String(v).toLowerCase())));
    }

    // Sort
    if (sortField) {
      rows = [...rows].sort((a, b) => {
        const av = a[sortField], bv = b[sortField];
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        if (typeof av === 'number' && typeof bv === 'number') return sortDir === 'asc' ? av - bv : bv - av;
        return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
      });
    }
    return rows;
  }, [allRows, typeFilter, globalSearch, colFilters, sortField, sortDir]);

  const total = filtered.length;
  const paginated = useMemo(() => filtered.slice((page - 1) * pageSize, page * pageSize), [filtered, page, pageSize]);

  // Stats by type
  const typeCounts = useMemo(() => {
    const counts = {};
    allRows.forEach(r => { counts[r.UserType] = (counts[r.UserType] || 0) + 1; });
    return counts;
  }, [allRows]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {/* Header bar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <GitBranch size={16} color="var(--brand-blue, #3b82f6)" />
          <span style={{ fontSize: 13, fontWeight: 800, color: 'var(--text-primary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Sub-Users (CLID)
          </span>
          <span style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'monospace' }}>
            WebApp.ClientMaster_Child
          </span>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            className="erp-btn erp-btn-primary"
            onClick={syncNow}
            disabled={syncing || loading}
            data-testid="subusers-sync-btn"
            style={{ gap: 6 }}
          >
            <RefreshCcw size={12} />
            {syncing ? 'Syncing…' : 'Sync from Tenant DBs'}
          </button>
          <button
            className="erp-btn"
            onClick={load}
            disabled={loading}
            data-testid="subusers-refresh-btn"
            style={{ gap: 6 }}
          >
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
      </div>

      {/* Sync result banner */}
      {syncResult && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 12px', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }}>
          <span style={{ fontWeight: 700, color: 'var(--green, #107C10)' }}>Sync result:</span>
          <span>{syncResult.synced} upserted · {syncResult.skipped} skipped · {syncResult.clients} clients scanned</span>
          {syncResult.errors?.length > 0 && (
            <span style={{ color: 'var(--red, #C42B1C)', marginLeft: 8 }}>{syncResult.errors.length} error(s)</span>
          )}
          <button onClick={() => setSyncResult(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
            <X size={12} />
          </button>
        </div>
      )}

      {/* Stats pills by user type */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        {USER_TYPES.map(ut => {
          const count = ut === 'All' ? allRows.length : (typeCounts[ut] || 0);
          const active = typeFilter === ut;
          return (
            <button
              key={ut}
              onClick={() => { setTypeFilter(ut); setPage(1); }}
              data-testid={`subusers-filter-${ut.toLowerCase()}`}
              style={{
                display: 'flex', alignItems: 'center', gap: 5,
                padding: '4px 10px', borderRadius: 100,
                fontSize: 11, fontWeight: 700, cursor: 'pointer',
                background: active ? 'var(--brand-blue, #0078D4)' : 'var(--surface)',
                color: active ? '#fff' : 'var(--text-secondary)',
                border: active ? 'none' : '1px solid var(--border)',
                transition: 'background 120ms, color 120ms'
              }}
            >
              {ut === 'All' ? <Users size={10} /> : null}
              {ut}
              <span style={{
                background: active ? 'rgba(255,255,255,0.25)' : 'var(--surface-2)',
                color: active ? '#fff' : 'var(--text-muted)',
                padding: '1px 5px', borderRadius: 100, fontSize: 10, fontWeight: 700
              }}>
                {count}
              </span>
            </button>
          );
        })}

        {/* Global search */}
        <div style={{ marginLeft: 'auto', position: 'relative' }}>
          <Search size={12} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none' }} />
          <input
            type="text"
            value={globalSearch}
            onChange={e => { setGlobalSearch(e.target.value); setPage(1); }}
            placeholder="Search any field…"
            data-testid="subusers-global-search"
            style={{
              paddingLeft: 28, paddingRight: globalSearch ? 28 : 10,
              padding: '5px 10px 5px 28px',
              border: '1px solid var(--border)',
              borderRadius: 8, fontSize: 12,
              background: 'var(--surface)', color: 'var(--text-primary)',
              outline: 'none', minWidth: 200
            }}
          />
          {globalSearch && (
            <button
              onClick={() => { setGlobalSearch(''); setPage(1); }}
              style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 2 }}
            >
              <X size={11} />
            </button>
          )}
        </div>
      </div>

      {/* Grid */}
      <ERPAdvancedGrid
        title={`Sub-Users / CLID (${total} shown${typeFilter !== 'All' ? ` · ${typeFilter}` : ''}${globalSearch ? ` · "${globalSearch}"` : ''})`}
        columns={COLUMNS}
        data={paginated}
        total={total}
        idField="SlNo"
        nameField="ClientName"
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        sortField={sortField}
        sortDir={sortDir}
        onSort={onSort}
        filters={colFilters}
        onFilterChange={(f) => { setColFilters(f); setPage(1); }}
        loading={loading}
        showAddNew={false}
        testId="subusers-grid"
        cellRenderer={(field, value) => {
          if (field === 'UserType') {
            const colorMap = {
              LICENSEE: '#10b981', ADMINSUPPORT: '#f59e0b',
              LICENSOR: '#3b82f6', MANAGER: '#8b5cf6'
            };
            return (
              <span style={{
                display: 'inline-flex', alignItems: 'center',
                padding: '2px 8px', borderRadius: 100,
                fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                background: `${colorMap[value] || '#94a3b8'}20`,
                color: colorMap[value] || '#94a3b8',
                border: `1px solid ${colorMap[value] || '#94a3b8'}40`
              }}>
                {value}
              </span>
            );
          }
          if (field === 'Status') {
            return (
              <span style={{
                display: 'inline-flex', alignItems: 'center',
                padding: '2px 8px', borderRadius: 100,
                fontSize: 10, fontWeight: 700,
                background: value === 'Active' ? '#f0fdf4' : '#fef2f2',
                color: value === 'Active' ? '#16a34a' : '#dc2626',
                border: `1px solid ${value === 'Active' ? '#bbf7d0' : '#fecaca'}`
              }}>
                {value}
              </span>
            );
          }
          return null;
        }}
      />
    </div>
  );
}
